import { useEffect, useState } from 'react';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { Container, Section } from '@components';
import { db } from '../services/firebase';
import usePageMeta from '@/utils/usePageMeta';

const DEFAULT_LIMIT = 4;

const News = () => {
  usePageMeta({
    title: 'News — Data & AI Updates | DataLogicHub',
    description:
      'The latest data and AI industry news, curated and summarized by DataLogicHub.',
  });

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const q = query(
          collection(db, 'ai_news'),
          orderBy('pubDate', 'desc'),
          limit(DEFAULT_LIMIT),
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        setNews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Failed to load AI news:', err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-primary-navy text-white min-h-screen">
      <Section className="py-16 md:py-20">
        <Container>
          <div className="mb-10 md:mb-12 text-center">
            <p className="text-xs font-semibold tracking-widest text-primary-teal mb-2">
              LATEST NEWS
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              AI Automation, today
            </h1>
            <p className="text-slate-300 mt-3 max-w-2xl mx-auto">
              Curated headlines on AI automation from across the web, refreshed every day.
            </p>
          </div>

          {loading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {Array.from({ length: DEFAULT_LIMIT }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-white/20 bg-white/10 overflow-hidden animate-pulse"
                >
                  <div className="aspect-video w-full bg-slate-700/50" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-24 bg-slate-700/50 rounded" />
                    <div className="h-4 w-full bg-slate-700/50 rounded" />
                    <div className="h-4 w-5/6 bg-slate-700/50 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <p className="text-center text-slate-300">
              Could not load news right now. Please try again later.
            </p>
          )}

          {!loading && !error && news.length === 0 && (
            <p className="text-center text-slate-300">
              No news articles available yet.
            </p>
          )}

          {!loading && !error && news.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {news.map((item) => {
                const href = item.link || '#';
                // Guard: a non-string pubDate (Firestore Timestamp/number)
                // must not crash the render (UIUX audit M2).
                const pubDate =
                  typeof item.pubDate === 'string'
                    ? new Date(item.pubDate.replace(' ', 'T') + 'Z')
                    : item.pubDate?.toDate?.() ?? null;
                return (
                  <a
                    key={item.id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group rounded-2xl border border-white/20 bg-white/15 overflow-hidden flex flex-col hover:border-primary-teal/50 hover:bg-white/20 transition-colors"
                  >
                    <div className="aspect-video w-full bg-slate-800 overflow-hidden">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.title || 'News image'}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">
                          No image
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                        {item.sourceName && (
                          <span className="font-semibold text-primary-teal truncate">
                            {item.sourceName}
                          </span>
                        )}
                        {pubDate && !isNaN(pubDate) && (
                          <>
                            <span aria-hidden>•</span>
                            <span>
                              {pubDate.toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          </>
                        )}
                      </div>
                      <h3 className="text-base font-bold text-white mb-2 line-clamp-3 group-hover:text-primary-teal transition-colors">
                        {item.title || 'Untitled'}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-slate-300 leading-relaxed line-clamp-3 mb-4">
                          {item.description}
                        </p>
                      )}
                      <span className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-primary-teal group-hover:text-teal-300">
                        Read more
                        <span aria-hidden>→</span>
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </Container>
      </Section>
    </div>
  );
};

export default News;
